import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { useAppSelector } from '../hooks/store';
import { isBarberLoggedIn, isCustomerLoggedIn } from '../slices/authSlice';
import { useGetCurrentUserQuery } from '../services/userService';
import { useGetCustomerMeQuery } from '../services/customerPortalService';
import { useSentryContext } from '../hooks/useSentryContext';
import { clearDash0Identity, identifyDash0User } from '../utils/observability';

interface SentryContextProviderProps {
    children: React.ReactNode;
}

/**
 * Provider component that sets up Sentry user context
 * and enriches it with user data from API when available
 */
export const SentryContextProvider = ({ children }: SentryContextProviderProps) => {
    // Set basic context from JWT tokens
    useSentryContext();

    const isBarber = useAppSelector(isBarberLoggedIn);
    const isCustomer = useAppSelector(isCustomerLoggedIn);

    // Fetch user details when logged in
    const { data: barberData } = useGetCurrentUserQuery(undefined, {
        skip: !isBarber,
    });

    const { data: customerData } = useGetCustomerMeQuery(undefined, {
        skip: !isCustomer,
    });

    // Enrich Sentry context with additional user data
    useEffect(() => {
        if (import.meta.env.DEV) {
            return;
        }

        if (isBarber && barberData) {
            Sentry.setContext('barber', {
                id: barberData.id,
                firstName: barberData.first_name,
                lastName: barberData.last_name,
                email: barberData.email,
                barberShopId: barberData.barber_shop?.id,
                barberShopName: barberData.barber_shop?.name,
            });
            // Tags are filterable in the Sentry UI; context fields are not.
            Sentry.setTag('barber_shop_id', barberData.barber_shop?.id ?? 'unknown');
        } else if (!isBarber) {
            Sentry.setContext('barber', null);
            Sentry.setTag('barber_shop_id', undefined);
        }
    }, [isBarber, barberData]);

    useEffect(() => {
        if (import.meta.env.DEV) {
            return;
        }

        if (isCustomer && customerData) {
            // Phone intentionally omitted — PII; not needed for triage.
            Sentry.setContext('customer', {
                id: customerData.id,
                firstName: customerData.first_name,
                lastName: customerData.last_name,
                email: customerData.email,
            });
        } else if (!isCustomer) {
            Sentry.setContext('customer', null);
        }
    }, [isCustomer, customerData]);

    // Dash0 user identity — not DEV-gated like the Sentry blocks above; the
    // SDK itself is env-gated (no VITE_DASH0_* vars → every call is a no-op),
    // so a locally-configured dev build still gets identified sessions.
    useEffect(() => {
        if (isBarber && barberData) {
            identifyDash0User(barberData, 'barber', barberData.barber_shop?.id);
        } else if (isCustomer && customerData) {
            identifyDash0User(customerData, 'customer');
        } else if (!isBarber && !isCustomer) {
            clearDash0Identity();
        }
    }, [isBarber, isCustomer, barberData, customerData]);

    return <>{children}</>;
};
